import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, getCookie } from '../services/auth.js';

type AuthState = 'loading' | 'authenticated' | 'unauthenticated';

// True when the exp cookie exists, even if the access token is already expired.
// That implies there may be a valid HttpOnly refresh token on the server.
function hasSession(): boolean {
  return getCookie('vault_token_exp') !== null;
}

function getInitialState(): AuthState {
  if (auth.isAuthenticated()) return 'authenticated';
  // Access token expired but a session cookie exists — attempt server-side refresh.
  if (hasSession()) return 'loading';
  return 'unauthenticated';
}

export function useAuth() {
  const [state, setState] = useState<AuthState>(getInitialState);

  // On mount or when state changes to authenticated/loading: initialize API token.
  // Handles both cases:
  //   - 'loading': access token expired → attempt server-side refresh
  //   - 'authenticated': valid cookies on page load → read token immediately
  // On mount: confirm auth state (handles expired access token with valid refresh cookie).
  useEffect(() => {
    if (state !== 'loading' && state !== 'authenticated') return;

    auth.getTokens().then((tokens) => {
      setState(tokens ? 'authenticated' : 'unauthenticated');
    });
  }, [state]);

  // Re-check auth on window focus and visibilitychange (user returns after a long time).
  useEffect(() => {
    const check = () => {
      if (document.visibilityState !== 'visible') return;
      auth.getTokens().then((tokens) => {
        setState(tokens ? 'authenticated' : 'unauthenticated');
      });
    };

    window.addEventListener('focus', check);
    document.addEventListener('visibilitychange', check);
    return () => {
      window.removeEventListener('focus', check);
      document.removeEventListener('visibilitychange', check);
    };
  }, []);

  return {
    isAuthenticated: state === 'authenticated',
    isLoading: state === 'loading',
    login: auth.login.bind(auth),
    logout: auth.logout.bind(auth),
  };
}

// Callback handler hook.
// The OAuth callback is now handled entirely by the backend Lambda, which
// exchanges the code for tokens, sets cookies, and redirects to /.
// If the React /callback route is ever hit (e.g. during local dev fallback),
// we simply navigate home so the app can read the cookies.
export function useAuthCallback() {
  const navigate = useNavigate();

  useEffect(() => {
    navigate('/', { replace: true });
  }, [navigate]);

  return { error: null };
}
